import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";

const projects = [
  {
    id: "01",
    title: "VelocityAI Platform",
    category: "Workforce Intelligence",
    description: "Enterprise platform delivering workforce intelligence with AI-driven causal attribution and analytics to optimize training, rostering, and productivity — enabling measurable business impact.",
    image: "/Velocity-ai.jpeg",
    link: "https://joinvelocity.co",
    tags: ["AI", "Workforce Intelligence", "Causal Attribution", "Analytics", "SaaS"],
    color: "#2563EB" // Blue
  },
  {
    id: "02",
    title: "Climformatics",
    category: "AI Climate Intelligence",
    description: "Orchestrated the product roadmap for an AI-driven climate prediction engine. Led a team of 12 engineers to reduce prediction latency by 40% while achieving 95% forecast accuracy for enterprise logistics clients.",
    image: "https://static.wixstatic.com/media/cb3ba5_346287b49d88475993acf12c2944d872~mv2.png/v1/fill/w_1200,h_800,al_c,q_90/climat-dashboard.jpg",
    link: "https://climformatics.com/",
    tags: ["B2B SaaS", "Python", "Predictive ML"],
    color: "#0284c7" // Sky Blue
  },
  {
    id: "03",
    title: "BestDay2Marry",
    category: "Consumer Wedding Tech",
    description: "Launched a D2C platform forecasting weather for wedding venues 12 months out. Defined monetization strategy and user loops, driving 10k+ monthly active users within the first quarter of launch.",
    image: "https://static.wixstatic.com/media/cb3ba5_1982090ca6df49e089dba2a63f5dea63~mv2.png/v1/fill/w_1200,h_800,al_c,q_90/wedding-app.jpg",
    link: "https://bestday2marry.com/",
    tags: ["Consumer App", "Growth Strategy", "React Native"],
    color: "#e11d48" // Rose Red
  },
  {
    id: "04",
    title: "Momento Frames",
    category: "E-Commerce Experience",
    description: "Spearheaded the end-to-end digital transformation of a custom framing business. Optimized the checkout funnel via A/B testing, resulting in an 18% lift in conversion rates and $200k annualized revenue.",
    image: "https://static.wixstatic.com/media/cb3ba5_1762b20604c54e6595162c9698a430a1~mv2.png/v1/fill/w_1200,h_800,al_c,q_90/momento-frames.jpg",
    link: "https://momento.ink/",
    tags: ["E-Commerce", "UX Research", "Figma"],
    color: "#d97706" // Amber
  },
];

const Projects = () => {
  return (
    <section id="projects" className="bg-white py-32 px-4 md:px-8 overflow-hidden">
      {/* Section Header */}
      <div className="max-w-7xl mx-auto mb-32">
        <motion.h2 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false }} // Changed to false so header also replays
          className="text-6xl md:text-8xl font-bold text-slate-900 tracking-tighter"
        >
          Selected<br />Works.
        </motion.h2>
      </div>

      {/* Projects List */}
      <div className="flex flex-col gap-32 max-w-7xl mx-auto">
        {projects.map((project, index) => (
          <ProjectItem key={project.id} project={project} index={index} />
        ))}
      </div>
    </section>
  );
};

const ProjectItem = ({ project, index }) => {
  const isEven = index % 2 === 0;

  return (
    <div className={`flex flex-col lg:flex-row gap-12 lg:gap-24 items-center ${isEven ? "" : "lg:flex-row-reverse"}`}>
      
      {/* --- IMAGE SECTION (The Replaying Curtain Reveal) --- */}
      <div className="w-full lg:w-3/5 relative group cursor-pointer">
        <a href={project.link} target="_blank" rel="noopener noreferrer" className="block overflow-hidden rounded-2xl relative">
          
          {/* The Image */}
          <motion.img
            src={project.image}
            alt={project.title}
            className="w-full h-[400px] md:h-[600px] object-cover grayscale group-hover:grayscale-0 transition-all duration-700 ease-in-out transform group-hover:scale-105"
            // Animation States
            initial={{ scale: 1.2 }}
            whileInView={{ scale: 1 }}
            // The Key Change: once: false ensures it resets when you scroll away
            viewport={{ once: false, amount: 0.4 }} 
            transition={{ duration: 1.5 }}
          />

          {/* The "Curtain" Overlay Animation */}
          <motion.div
            // Animation States
            initial={{ width: "100%" }}
            whileInView={{ width: "0%" }}
            // The Key Change: once: false makes the curtain close and reopen
            viewport={{ once: false, amount: 0.4 }} 
            transition={{ duration: 1, ease: "circOut", delay: 0.2 }}
            style={{ backgroundColor: project.color }}
            className="absolute inset-0 z-10"
          />
        </a>
      </div>

      {/* --- TEXT SECTION --- */}
      <div className="w-full lg:w-2/5 flex flex-col justify-center">
        
        {/* Project Number */}
        <motion.span 
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: false, amount: 0.5 }} // Text also replays
          className="text-sm font-mono font-bold tracking-widest mb-6 block"
          style={{ color: project.color }}
        >
          /{project.id} — {project.category}
        </motion.span>

        {/* Title with Link Hover */}
        <a href={project.link} target="_blank" rel="noopener noreferrer" className="group/title inline-block">
          <h3 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight group-hover/title:text-slate-600 transition-colors">
            {project.title}
            <ArrowUpRight className="inline-block ml-4 w-8 h-8 opacity-0 -translate-y-2 translate-x-2 group-hover/title:opacity-100 group-hover/title:translate-y-0 group-hover/title:translate-x-0 transition-all duration-300" />
          </h3>
        </a>

        {/* Description */}
        <p className="text-lg text-slate-600 leading-relaxed mb-8">
          {project.description}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-3">
          {project.tags.map((tag, idx) => (
            <span 
              key={idx} 
              className="px-4 py-2 border border-slate-200 rounded-full text-xs font-bold uppercase tracking-wider text-slate-500 hover:border-slate-400 hover:text-slate-900 transition-colors"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Projects;